import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { get } from "lodash";
import ServiceProvider from "@client.services/provider";
import { getError } from "@client.utils/errors";

const DEFAULT_DATA = { data: [], load: false };

const initialState = {
  insights: DEFAULT_DATA,
  error: null,
  isExportLoading: false,
  insightEvents: DEFAULT_DATA,
  insightEventResponses: { eventResponses: [], attendeesQty: 0, acceptancePercentage: 0 },
  selectedInsight: null,
};

export const getInsights = createAsyncThunk(
  "dashboard/insights",
  async (filter) => {
    return ServiceProvider.Notification.getAll(filter);
  }
);

export const regenerateInsights = createAsyncThunk(
  "dashboard/insights/regenerate",
  async (filter) => {
    return ServiceProvider.Notification.regenerate(filter);
  }
);

export const markAsRead = createAsyncThunk(
  "dashboard/insights/read",
  async ({ id, value }) => {
    return await ServiceProvider.Notification.markAsRead(id, value);
  }
);

export const exportAsync = createAsyncThunk(
  "dashboard/export",
  async ({ filter, language, isCostIndexPresentation }) => {
    return await ServiceProvider.Meetings.export(
      filter,
      language,
      isCostIndexPresentation
    );
  }
);

export const getInsightEvents = createAsyncThunk(
  "dashboard/insights/events",
  async (id) => {
    return await ServiceProvider.Notification.getInsightEvents(id);
  }
);

export const getInsightEventResponses = createAsyncThunk(
  "dashboard/insights/eventResponses",
  async (id) => {
    return await ServiceProvider.Notification.getInsightEventResponses(id);
  }
);

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setDashboardError: (state, action) => {
      state.error = get(action, "payload", null);
    },
    setSelectedInsight: (state, action) => {
      state.selectedInsight = action.payload;
    },
    clearSelectedInsight: (state) => {
      state.selectedInsight = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInsights.pending, (state) => {
        state.insights.load = true;
      })
      .addCase(getInsights.fulfilled, (state, action) => {
        state.insights = {
          data: get(action, "payload", []),
          load: false,
        };
      })
      .addCase(getInsights.rejected, (state, action) => {
        state.insights = { ...DEFAULT_DATA };
        state.error = getError(action);
      })
      .addCase(regenerateInsights.pending, (state) => {
        state.insights.load = true;
      })
      .addCase(regenerateInsights.fulfilled, (state, action) => {
        state.insights = {
          data: get(action, "payload", []),
          load: false,
        };
      })
      .addCase(regenerateInsights.rejected, (state, action) => {
        state.insights = { ...DEFAULT_DATA };
        state.error = getError(action);
      })
      .addCase(exportAsync.pending, (state) => {
        state.isExportLoading = true;
      })
      .addCase(exportAsync.fulfilled, (state) => {
        state.isExportLoading = false;
      })
      .addCase(exportAsync.rejected, (state, action) => {
        state.isExportLoading = false;
        state.error = getError(action);
      })
      .addCase(getInsightEvents.pending, (state) => {
        state.insightEvents = { data: [], load: true };
      })
      .addCase(getInsightEvents.fulfilled, (state, action) => {
        state.insightEvents = {
          data: get(action, "payload", []),
          load: false,
        };
      })
      .addCase(getInsightEvents.rejected, (state, action) => {
        state.insightEvents = { ...DEFAULT_DATA };
        state.error = getError(action);
      })
      .addCase(getInsightEventResponses.pending, (state) => {
        state.insightEventResponses = { eventResponses: [], attendeesQty: 0, acceptancePercentage: 0 };
      })
      .addCase(getInsightEventResponses.fulfilled, (state, action) => {
        state.insightEventResponses = get(action, "payload", { eventResponses: [], attendeesQty: 0, acceptancePercentage: 0 })
      })
      .addCase(getInsightEventResponses.rejected, (state, action) => {
        state.insightEventResponses = { eventResponses: [], attendeesQty: 0, acceptancePercentage: 0 };
        state.error = getError(action);
      });
  },
});

export const { setDashboardError, setSelectedInsight, clearSelectedInsight } = dashboardSlice.actions;

export const makeInsights = (state) => state.dashboard.insights;
export const makeDashboardError = (state) => state.dashboard.error;
export const makeIsExportLoading = (state) => state.dashboard.isExportLoading;
export const makeInsightEvents = (state) => state.dashboard.insightEvents;
export const makeInsightEventResponses = (state) => state.dashboard.insightEventResponses;
export const makeSelectedInsight = (state) => state.dashboard.selectedInsight;

export default dashboardSlice.reducer;
